import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { orderBy, where } from "firebase/firestore";
import { subscribeToTickets } from "@/lib/firestore";
import { useAuth } from "@/lib/auth";
import { useDocumentTitle } from "@/lib/useDocumentTitle";
import { Ticket, TicketStatus, TicketPriority } from "@/types";
import { TicketTable } from "./Dashboard";

const STATUS_FILTERS: Array<{ label: string; value: TicketStatus | "all" }> = [
  { label: "All", value: "all" },
  { label: "Open", value: "open" },
  { label: "In Progress", value: "in_progress" },
  { label: "Pending User", value: "pending_user" },
  { label: "Resolved", value: "resolved" },
];

const PRIORITY_PILLS: Array<{ label: string; value: TicketPriority }> = [
  { label: "P1 Critical", value: "P1" },
  { label: "P2 High",     value: "P2" },
  { label: "P3 Medium",   value: "P3" },
  { label: "P4 Low",      value: "P4" },
];

export default function Tickets() {
  const navigate = useNavigate();
  const location = useLocation();
  const { firebaseUser } = useAuth();
  const isMyTickets = location.pathname === "/my-tickets";
  useDocumentTitle(isMyTickets ? "My Tickets" : "All Tickets");
  const [searchParams, setSearchParams] = useSearchParams();

  const statusFilter = (searchParams.get("status") as TicketStatus | "all") || "all";
  const priorityFilter = (searchParams.get("priority") as TicketPriority | "all") || "all";
  const since = searchParams.get("since") || ""; // "today" supported

  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [search, setSearch] = useState("");

  const setFilter = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value && value !== "all") next.set(key, value);
    else next.delete(key);
    // Clicking a status pill clears the dashboard-only `since=today` shortcut
    // so users aren't surprised by a sticky time-window filter.
    if (key === "status") next.delete("since");
    setSearchParams(next, { replace: true });
  };

  const clearAllFilters = () => setSearchParams(new URLSearchParams(), { replace: true });
  const hasFilters = statusFilter !== "all" || priorityFilter !== "all" || since !== "" || search !== "";

  useEffect(() => {
    const constraints = [orderBy("createdAt", "desc")];
    if (statusFilter !== "all") constraints.unshift(where("status", "==", statusFilter) as never);
    if (isMyTickets && firebaseUser) constraints.unshift(where("submitterId", "==", firebaseUser.uid) as never);
    const unsub = subscribeToTickets(constraints, setTickets);
    return unsub;
  }, [statusFilter, isMyTickets, firebaseUser]);

  const sinceMs = since === "today"
    ? new Date(new Date().setHours(0, 0, 0, 0)).getTime()
    : null;

  const filtered = tickets.filter((t) => {
    if (priorityFilter !== "all" && t.priority !== priorityFilter) return false;
    if (sinceMs !== null) {
      // For "today" we filter on resolvedAt for resolved tickets, otherwise createdAt
      const ms = t.resolvedAt?.toMillis() ?? t.createdAt?.toMillis();
      if (!ms || ms < sinceMs) return false;
    }
    if (search && !t.title.toLowerCase().includes(search.toLowerCase()) &&
        !t.submitterEmail.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  const headerSuffix = since === "today" ? " — today" : "";
  const title = isMyTickets ? "My Tickets" : "All Tickets";

  return (
    <div style={{ overflowY: "auto", flex: 1, padding: 20 }}>
      <h1 style={{ marginBottom: 18 }}>{title}{headerSuffix}</h1>

      <div style={{ display: "flex", gap: 8, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <input
          className="fi" style={{ width: 200 }}
          placeholder="Search tickets…"
          value={search} onChange={(e) => setSearch(e.target.value)}
        />

        {STATUS_FILTERS.map((f) => (
          <button
            key={f.value}
            onClick={() => setFilter("status", f.value)}
            style={pillStyle(statusFilter === f.value, "var(--blue)")}
          >
            {f.label}
          </button>
        ))}

        {PRIORITY_PILLS.map((p) => (
          <button
            key={p.value}
            onClick={() => setFilter("priority", priorityFilter === p.value ? null : p.value)}
            style={pillStyle(
              priorityFilter === p.value,
              p.value === "P1" ? "var(--red)" : p.value === "P2" ? "var(--orange)" : p.value === "P3" ? "var(--blue)" : "var(--text3)",
            )}
          >
            {p.label}
          </button>
        ))}

        {since === "today" && (
          <button
            onClick={() => setFilter("since", null)}
            style={{ fontSize: 12, padding: "5px 12px", borderRadius: 20, border: "1px solid var(--green)",
              background: "var(--green)", color: "white", cursor: "pointer", fontFamily: "var(--font)" }}
          >
            Today only ×
          </button>
        )}

        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button className="btn btn-primary btn-sm" onClick={() => navigate("/tickets/new")}>+ New Ticket</button>
        </div>
      </div>

      <div style={{ fontSize: 12, color: "var(--text2)", marginBottom: 8, display: "flex", alignItems: "center", gap: 8 }}>
        <span>{filtered.length} ticket{filtered.length !== 1 ? "s" : ""}</span>
        {tickets.length > 0 && filtered.length !== tickets.length && (
          <span style={{ color: "var(--text3)" }}>· {tickets.length - filtered.length} hidden by filters</span>
        )}
        {hasFilters && (
          <button onClick={clearAllFilters}
            style={{ marginLeft: "auto", background: "none", border: "none", color: "var(--blue)", fontSize: 12, cursor: "pointer", padding: 0, fontFamily: "var(--font)" }}>
            Clear all filters
          </button>
        )}
      </div>

      {filtered.length === 0 && hasFilters ? (
        <div className="card" style={{ padding: "32px 16px", textAlign: "center", color: "var(--text2)", fontSize: 13 }}>
          <div style={{ marginBottom: 6 }}>No tickets match the active filters.</div>
          <div style={{ fontSize: 12, marginBottom: 12 }}>
            Active: {[
              statusFilter !== "all" && `status: ${statusFilter}`,
              priorityFilter !== "all" && `priority: ${priorityFilter}`,
              since && `since: ${since}`,
              search && `search: "${search}"`,
            ].filter(Boolean).join(" · ")}
          </div>
          <button className="btn btn-secondary btn-sm" onClick={clearAllFilters}>Clear all filters</button>
        </div>
      ) : (
        <TicketTable tickets={filtered} onSelect={(id) => navigate(`/tickets/${id}`)} />
      )}
    </div>
  );
}

function pillStyle(active: boolean, accent: string): React.CSSProperties {
  return {
    fontSize: 12, padding: "5px 12px", borderRadius: 20,
    border: `1px solid ${active ? accent : "var(--border-med)"}`,
    background: active ? accent : "var(--surface)",
    color: active ? "white" : "var(--text2)",
    cursor: "pointer", fontFamily: "var(--font)",
  };
}
